import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, DollarSign, Save, Pencil, History, Plus, Trash2, Target, Layers, Building2, Percent, HardHat, Shield } from "lucide-react";
import { toast } from "sonner";

const RATE_FIELDS = [
  ["metre_rate", "Metre Rate ($/m)"],
  ["standard_rate", "Standard Hourly (Mon–Fri)"],
  ["saturday_rate", "Saturday Hourly"],
  ["overtime_rate", "Approved Overtime"],
  ["travel_rate", "Approved Travel"],
];
const money = (v) => (v === null || v === undefined || v === "" ? "—" : `$${Number(v).toFixed(2)}`);

export default function FinancialSettings() {
  return (
    <div className="space-y-6 animate-fade-up" data-testid="financial-settings">
      <div className="flex items-center gap-3">
        <Link to="/settings" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><ArrowLeft className="w-5 h-5" /></Link>
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy flex items-center gap-2"><DollarSign className="w-7 h-7" />Financial Settings</h1>
          <p className="text-slate-500 mt-1">Labour rates, overhead recovery, material costs, targets & job-costing rules.</p>
        </div>
      </div>

      <Tabs defaultValue="labour">
        <TabsList className="flex-wrap h-auto" data-testid="financial-tabs">
          <TabsTrigger value="labour" data-testid="tab-labour">Labour Rates</TabsTrigger>
          <TabsTrigger value="overhead" data-testid="tab-overhead">Overhead Recovery</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials">Material Costs</TabsTrigger>
          <TabsTrigger value="targets" data-testid="tab-targets">Financial Targets</TabsTrigger>
          <TabsTrigger value="recovery" data-testid="tab-recovery">Cost Recovery Rules</TabsTrigger>
          <TabsTrigger value="costing" data-testid="tab-costing">Job Costing Rules</TabsTrigger>
        </TabsList>
        <TabsContent value="labour" className="mt-6"><LabourRates /></TabsContent>
        <TabsContent value="overhead" className="mt-6"><Overhead /></TabsContent>
        <TabsContent value="materials" className="mt-6"><MaterialCosts /></TabsContent>
        <TabsContent value="targets" className="mt-6"><Targets /></TabsContent>
        <TabsContent value="recovery" className="mt-6"><CostRecovery /></TabsContent>
        <TabsContent value="costing" className="mt-6"><JobCosting /></TabsContent>
      </Tabs>
    </div>
  );
}

function LabourRates() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [history, setHistory] = useState(null);
  const load = () => api.get("/financial/labour-rates").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const emps = rows.filter((r) => r.worker_type === "employee");
  const cons = rows.filter((r) => r.worker_type === "contractor");

  const Section = ({ title, icon: Icon, list }) => (
    <div className="space-y-3">
      <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Icon className="w-4 h-4" />{title}</h3>
      {list.length === 0 ? <p className="text-sm text-slate-400">None.</p> : (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr><th className="text-left px-4 py-3">Name</th><th className="px-3 py-3">Metre</th><th className="px-3 py-3">Standard</th><th className="px-3 py-3">Sat</th><th className="px-3 py-3">O/T</th><th className="px-3 py-3">Travel</th><th className="px-3 py-3">Super</th><th className="px-3 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((r) => (
                  <tr key={r.id} data-testid={`labour-row-${r.id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3"><p className="font-medium text-navy">{r.name}</p><p className="text-xs text-slate-400 capitalize">{r.role}</p></td>
                    {RATE_FIELDS.map(([k]) => <td key={k} className="px-3 py-3 text-center text-slate-600">{money(r.labour_rates[k])}</td>)}
                    <td className="px-3 py-3 text-center">{r.labour_rates.includes_super ? "Yes" : "No"}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button data-testid={`edit-labour-${r.id}`} onClick={() => setEdit(r)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                      {r.history_count > 0 && <button data-testid={`history-labour-${r.id}`} onClick={() => setHistory(r)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><History className="w-4 h-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">Rate changes are snapshotted to history and never recalculate previous jobs.</p>
      <Section title="Employees" icon={Shield} list={emps} />
      <Section title="Contractors" icon={HardHat} list={cons} />
      {edit && <EditRatesDialog row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {history && <HistoryDialog row={history} onClose={() => setHistory(null)} />}
    </div>
  );
}

function EditRatesDialog({ row, onClose, onSaved }) {
  const [f, setF] = useState({
    ...Object.fromEntries(RATE_FIELDS.map(([k]) => [k, row.labour_rates[k] ?? ""])),
    includes_super: !!row.labour_rates.includes_super,
    effective_date: row.labour_rates.effective_date || "",
    notes: row.labour_rates.notes || "",
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/financial/labour-rates/${row.worker_type}/${row.id}`, f);
      toast.success("Rates updated"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{row.name} — Labour Rates</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {RATE_FIELDS.map(([k, label]) => (
              <div key={k}><Label className="text-xs">{label}</Label>
                <Input data-testid={`rate-input-${k}`} type="number" step="0.01" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" /></div>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-2.5">
            <Label>Rate Includes Super</Label>
            <Switch data-testid="rate-super-switch" checked={f.includes_super} onCheckedChange={(v) => setF({ ...f, includes_super: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Effective Date</Label><Input data-testid="rate-effective-date" type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs">Administrator Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1" /></div>
          <div><Label className="text-xs">Reason for change (audit)</Label><Input data-testid="rate-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="mt-1" placeholder="Optional" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="save-labour-rates" onClick={save} disabled={busy} className="bg-navy hover:bg-navy-light"><Save className="w-4 h-4 mr-1.5" />Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ row, onClose }) {
  const [hist, setHist] = useState([]);
  useEffect(() => { api.get(`/financial/labour-rates/${row.worker_type}/${row.id}/history`).then((r) => setHist(r.data)); }, []);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{row.name} — Rate History</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {hist.length === 0 ? <p className="text-slate-400 text-sm">No history.</p> : hist.map((h, i) => (
            <div key={i} className="rounded-xl border border-slate-100 p-3 text-sm">
              <div className="flex justify-between text-xs text-slate-400"><span>{h.changed_at?.slice(0, 10)} · {h.changed_by}</span>{h.effective_date && <span>Eff: {h.effective_date}</span>}</div>
              <div className="flex flex-wrap gap-3 mt-1.5 text-slate-600">
                {RATE_FIELDS.map(([k, label]) => <span key={k}>{label.split(" (")[0]}: <b>{money(h[k])}</b></span>)}
              </div>
              {h.reason && <p className="text-xs text-slate-400 mt-1">Reason: {h.reason}</p>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm max-w-2xl">
      <h3 className="font-heading font-semibold text-navy flex items-center gap-2 mb-4"><Icon className="w-5 h-5" />{title}</h3>
      {children}
    </div>
  );
}

function Overhead() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/financial/overhead").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/financial/overhead", f); setF({ ...f, ...r.data }); toast.success("Overhead saved"); const h = await api.get("/financial/overhead"); setF(h.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card title="Business Overhead Recovery" icon={Building2} testid="card-overhead">
      <p className="text-sm text-slate-500 mb-4">A single hourly value that recovers all business running costs across productive labour hours. Snapshotted against each completed job.</p>
      <Label className="text-xs">Hourly Business Overhead Recovery ($ / productive hour)</Label>
      <Input data-testid="overhead-rate" type="number" step="0.01" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} className="mt-1 mb-4 max-w-xs" />
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div><Label className="text-xs">Effective Date</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} className="mt-1" /></div>
      </div>
      <div className="mt-3"><Label className="text-xs">Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1" /></div>
      <Button data-testid="save-overhead" onClick={save} disabled={busy} className="mt-4 bg-navy hover:bg-navy-light"><Save className="w-4 h-4 mr-1.5" />Save</Button>
      {f.history?.length > 0 && (
        <div className="mt-6"><p className="text-xs uppercase tracking-wider text-slate-400 mb-2">History</p>
          <div className="space-y-1.5">{f.history.map((h, i) => <p key={i} className="text-xs text-slate-500">{h.changed_at?.slice(0, 10)} · {h.changed_by} — {money(h.rate)}/hr {h.reason ? `(${h.reason})` : ""}</p>)}</div>
        </div>
      )}
    </Card>
  );
}

function MaterialCosts() {
  const [f, setF] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/financial/material-costs").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  return (
    <Card title="Material Cost Settings" icon={Layers} testid="card-materials">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500">Material costs are managed on the <span className="font-medium text-navy">Materials</span> tab and mirrored here automatically. These unit costs feed job profitability.</p>
        <Button variant="outline" size="sm" data-testid="go-to-materials" onClick={() => navigate("/materials")}><Layers className="w-4 h-4 mr-1.5" />Manage in Materials</Button>
      </div>
      <div className="rounded-xl border border-slate-100 divide-y divide-slate-100" data-testid="material-costs-mirror">
        {f.items.length === 0 && <p className="text-sm text-slate-400 px-4 py-3">No materials yet. Add them on the Materials tab.</p>}
        {f.items.map((it, i) => (
          <div key={i} data-testid={`material-row-${i}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
            <span className="flex-1 text-navy font-medium">{it.name}</span>
            {it.supplier && <span className="text-slate-400 text-xs">{it.supplier}</span>}
            <span className="text-slate-400 text-xs w-16">{it.unit}</span>
            <span className="text-navy font-semibold w-24 text-right">${(it.unit_cost || 0).toFixed(2)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Targets() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/financial/targets").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const F = [
    ["target_margin", "Target Gross Operating Margin %", "emerald"],
    ["warning_margin", "Warning Margin %", "amber"],
    ["critical_margin", "Critical Margin %", "red"],
    ["target_recovery_rate", "Target Recovery Rate %", "navy"],
  ];
  const save = async () => { setBusy(true); try { await api.put("/financial/targets", f); toast.success("Targets saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  return (
    <Card title="Financial Targets" icon={Target} testid="card-targets">
      <p className="text-sm text-slate-500 mb-4">Used for colour-coding job performance throughout the app.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {F.map(([k, label]) => (
          <div key={k}><Label className="text-xs">{label}</Label>
            <Input data-testid={`target-${k}`} type="number" step="0.1" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" /></div>
        ))}
      </div>
      <Button data-testid="save-targets" onClick={save} disabled={busy} className="mt-4 bg-navy hover:bg-navy-light"><Save className="w-4 h-4 mr-1.5" />Save</Button>
    </Card>
  );
}

function CostRecovery() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/financial/cost-recovery-rules").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const save = async () => { setBusy(true); try { await api.put("/financial/cost-recovery-rules", f); toast.success("Rules saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  return (
    <Card title="Cost Recovery Rules" icon={Percent} testid="card-recovery">
      <p className="text-sm text-slate-500 mb-4">Control how business overhead is recovered across labour hours.</p>
      <div className="space-y-3">
        <div><Label className="text-xs">Overhead recovered on</Label>
          <Select value={f.overhead_basis} onValueChange={(v) => setF({ ...f, overhead_basis: v })}>
            <SelectTrigger data-testid="overhead-basis" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="productive_hours">Productive hours</SelectItem>
              <SelectItem value="standard_hours">Standard hours only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-2.5"><Label>Include travel hours as productive</Label><Switch data-testid="incl-travel" checked={f.include_travel_in_productive} onCheckedChange={(v) => setF({ ...f, include_travel_in_productive: v })} /></div>
        <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-2.5"><Label>Include overtime hours as productive</Label><Switch data-testid="incl-overtime" checked={f.include_overtime_in_productive} onCheckedChange={(v) => setF({ ...f, include_overtime_in_productive: v })} /></div>
        <div><Label className="text-xs">Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1" /></div>
      </div>
      <Button data-testid="save-recovery" onClick={save} disabled={busy} className="mt-4 bg-navy hover:bg-navy-light"><Save className="w-4 h-4 mr-1.5" />Save</Button>
    </Card>
  );
}

function JobCosting() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/financial/job-costing-rules").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const save = async () => { setBusy(true); try { await api.put("/financial/job-costing-rules", f); toast.success("Rules saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  return (
    <Card title="Job Costing Rules" icon={DollarSign} testid="card-costing">
      <p className="text-sm text-slate-500 mb-4">Defaults applied when costing a completed job (overridable per job).</p>
      <div className="space-y-3">
        <div><Label className="text-xs">Default payment method</Label>
          <Select value={f.default_payment_method} onValueChange={(v) => setF({ ...f, default_payment_method: v })}>
            <SelectTrigger data-testid="default-payment-method" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hourly">Hourly</SelectItem>
              <SelectItem value="metre">Per metre</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Default break (minutes)</Label><Input data-testid="default-break" type="number" value={f.default_break_minutes} onChange={(e) => setF({ ...f, default_break_minutes: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Round hours to (e.g. 0.25)</Label><Input data-testid="round-hours" type="number" step="0.05" value={f.round_hours_to} onChange={(e) => setF({ ...f, round_hours_to: e.target.value })} className="mt-1" /></div>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-2.5"><Label>Auto-apply Saturday rate on Saturdays</Label><Switch data-testid="auto-saturday" checked={f.auto_saturday_rate} onCheckedChange={(v) => setF({ ...f, auto_saturday_rate: v })} /></div>
        <div><Label className="text-xs">Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1" /></div>
      </div>
      <Button data-testid="save-costing" onClick={save} disabled={busy} className="mt-4 bg-navy hover:bg-navy-light"><Save className="w-4 h-4 mr-1.5" />Save</Button>
    </Card>
  );
}
